import { UserClaims } from '@okta/okta-auth-js';
import {
  CommentResponse,
  CreateCommentInternalInput,
  Meta,
} from '@repo/api/open-calls/graphql';
import { showErrorToast } from '@repo/ui/components/ui/error-toast';
import { Review } from '@repo/ui/models/candidate';
import { createInitialState, InitialState } from '@repo/ui/state';
import { toast } from 'sonner';
import { create } from 'zustand';
import {
  CommentsService,
  QueryCoralcommentsInternalArgs,
} from '../../services/comments/service';
import { formatError } from '../error-handling';

const initialState = createInitialState<Review[]>([]);

export interface CommentsState extends InitialState<Review[]> {
  createComment: (variables: {
    input: CreateCommentInternalInput;
    user: UserClaims;
  }) => Promise<void>;
  fetchComments: (variables: QueryCoralcommentsInternalArgs) => Promise<void>;
  fetchComment: (variables: { id: string }) => Promise<void>;
  updateComment: (id: string, body: string) => Promise<void>;
  reset: () => void;
  replyComment: (
    parentID: string,
    parentRevisionID: string,
    body: string,
    submissionID: string,
  ) => Promise<void>;
}

export const store = create<CommentsState>((set, get) => ({
  data: initialState.data,
  error: initialState.error,
  loading: initialState.loading,
  createComment: async (variables: {
    input: CreateCommentInternalInput;
    user: UserClaims;
  }) => {
    set((state) => ({ ...state, error: '', loading: true }));
    try {
      const data = await CommentsService.createComment(variables);
      const review = extractReviewsFromComment(data);
      set((state) => ({ ...state, data: [...state.data, review] }));
      toast.message('Comment added successfully');
    } catch (error: unknown) {
      showErrorToast({ title: 'Unable to add comment' });
      set((state) => ({
        ...state,
        error: formatError(error),
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
  },
  fetchComments: async (variables: QueryCoralcommentsInternalArgs) => {
    set((state) => ({ ...state, error: '', loading: true }));
    try {
      const data = await CommentsService.fetchComments(variables);
      const reviews = data.map(extractReviewsFromComment);
      set((state) => ({ ...state, data: reviews ?? [] }));
    } catch (error: unknown) {
      showErrorToast({ title: 'Unable to fetch comments' });
      set((state) => ({
        ...state,
        error: formatError(error),
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
  },
  fetchComment: async (variables: { id: string }) => {
    set((state) => ({ ...state, error: '', loading: true }));
    try {
      const data = await CommentsService.fetchComment(variables);
      const review = extractReviewsFromComment(data);
      set((state) => ({ ...state, data: [...state.data, review] }));
    } catch (error: unknown) {
      showErrorToast({ title: 'Unable to fetch comment' });
      set((state) => ({
        ...state,
        error: formatError(error),
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
  },
  updateComment: async (id: string, body: string) => {
    set((state) => ({ ...state, error: '', loading: true }));
    try {
      await CommentsService.updateComment({
        input: { body, commentID: id },
      });
      // Optimistically update the local state
      // to reflect the changes made to the comment
      const comments = get().data;
      const commentIndex = comments.findIndex((r) => r.commentId === id);
      const newComment = { ...comments[commentIndex], comment: body };
      const newState = [
        ...comments.slice(0, commentIndex),
        newComment,
        ...comments.slice(commentIndex + 1),
      ] as Review[];
      set((state) => ({
        ...state,
        data: newState,
      }));
    } catch (error: unknown) {
      set((state) => ({
        ...state,
        error: formatError(error),
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
  },
  reset: () => {
    set((state) => ({
      ...state,
      data: initialState.data,
      error: initialState.error,
      loading: initialState.loading,
    }));
  },
  replyComment: async (
    parentID: string,
    parentRevisionID: string,
    body: string,
    submissionID: string,
  ) => {
    set((state) => ({ ...state, error: '', loading: true }));
    try {
      const data = await CommentsService.replyComment({
        input: {
          body,
          parentID: parentID,
          submissionID,
          parentRevisionID: parentRevisionID,
        },
      });
      const review = extractReviewsFromComment(data);
      set((state) => ({ ...state, data: [...state.data, review] }));
    } catch (error: unknown) {
      showErrorToast({ title: 'Unable to add reply' });
      set((state) => ({
        ...state,
        error: formatError(error),
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
  },
}));

function extractReviewsFromComment(comment: CommentResponse): Review {
  const revision = comment.revisions[0];
  const lastUpdate = comment.revisions[comment.revisions.length - 1];
  let body = revision?.body;
  let createdAt = revision?.createdAt;
  let id = revision?.id;
  if (lastUpdate) {
    body = lastUpdate.body;
    createdAt = lastUpdate.createdAt;
    id = lastUpdate.id;
  }
  if (!id) {
    throw new Error('Comment ID is missing');
  }
  const author = revision?.meta ?? ({ createdBy: {} } as Meta);
  const authorName =
    author.createdBy?.name ??
    author.createdBy?.username ??
    author.createdBy?.email ??
    'Anonymous';
  const review: Review = {
    id: id,
    commentId: comment.id,
    createdAt,
    jury: authorName,
    rating: comment.rating ?? 0,
    comment: body ?? '',
    replies: [],
    parentID: comment.parentID ?? '',
    parentRevisionID: comment.parentRevisionID ?? '',
  };
  return review;
}
